import { toGGScore, toGGDelta, getStatusLabel, getRiskColor, getScoreProgressPct, getScoreBgClass } from '../utils/score';

interface ScoreCardProps {
  label: string;
  score: number;         // internal 0-100
  previousScore?: number | null; // internal 0-100
  size?: 'sm' | 'md' | 'lg';
}

function getTheme(color: 'green' | 'yellow' | 'red') {
  switch (color) {
    case 'green': return { bg: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-600', badgeBg: 'bg-emerald-50 text-emerald-600' };
    case 'yellow': return { bg: 'bg-amber-50 border-amber-200', text: 'text-amber-600', badgeBg: 'bg-amber-50 text-amber-600' };
    case 'red': return { bg: 'bg-red-50 border-red-200', text: 'text-red-600', badgeBg: 'bg-red-50 text-red-600' };
  }
}

const sizeStyles = {
  sm: { card: 'p-3', score: 'text-2xl', label: 'text-xs' },
  md: { card: 'p-4', score: 'text-3xl', label: 'text-sm' },
  lg: { card: 'p-6', score: 'text-4xl', label: 'text-base' },
};

export default function ScoreCard({ label, score, previousScore, size = 'md' }: ScoreCardProps) {
  const ggScore = toGGScore(score);
  const color = getRiskColor(ggScore);
  const statusLabel = getStatusLabel(ggScore);
  const theme = getTheme(color);
  const styles = sizeStyles[size];

  const delta = previousScore != null ? score - previousScore : null;
  const ggDelta = delta != null ? toGGDelta(delta) : null;

  return (
    <div className={`rounded-2xl border ${theme.bg} ${styles.card}`}>
      <div className="flex items-center justify-between">
        <p className={`${styles.label} font-medium text-slate-500`}>{label}</p>
        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${theme.badgeBg}`}>
          {statusLabel}
        </span>
      </div>
      <div className="flex items-baseline gap-2 mt-2">
        <span className={`${styles.score} font-bold ${theme.text}`}>
          {ggScore}
        </span>
        <span className="text-xs text-slate-400">/900</span>
        {ggDelta != null && ggDelta !== 0 && (
          <span className={`text-sm font-medium ${ggDelta > 0 ? 'text-emerald-500' : 'text-red-500'}`}>
            {ggDelta > 0 ? '\u25B2' : '\u25BC'} {Math.abs(ggDelta)}
          </span>
        )}
      </div>
      {/* Progress bar */}
      <div className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${getScoreBgClass(color)}`}
          style={{ width: `${getScoreProgressPct(ggScore)}%` }}
        />
      </div>
    </div>
  );
}
